
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import ContactMarquee from "../../../Components/CommonComponent/Table/ContactMarquee";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CommonContext } from "../../../App";
import api from "../../../services/api";
import toast from "react-hot-toast";

export default function ShowUpdationHistory({ latestEntry }) {
  console.log("latest entry",latestEntry)
  const { t } = useTranslation();
  // console.log(latestEntry)
  const { branchId } = useContext(CommonContext);

  const [allHistory, setAllHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Determine entry type
  const entryType = useMemo(() => {
    if (latestEntry?.receipts) return 'receipt';
    if (latestEntry?.payments) return 'payment';
    if (latestEntry?.sales) return 'sales';
    if (latestEntry?.purchases) return 'purchase';
    if (latestEntry?.journalFrom || latestEntry?.journalTo) return 'journal';
    return null;
  }, [latestEntry]);

  // Get ID and Key based on entry type
  const { Id, Key } = useMemo(() => {
    if (entryType === 'receipt') {
      return {
        Id: latestEntry?.receipts?.[0]?.id,
        Key: latestEntry?.receipts?.[0]?.receipt_key
      };
    }
    if (entryType === 'payment') {
      return {
        Id: latestEntry?.payments?.[0]?.id,
        Key: latestEntry?.payments?.[0]?.payment_key
      };
    }
    if (entryType === 'sales') {
      return {
        Id: latestEntry?.sales?.[0]?.id,
        Key: latestEntry?.sales?.[0]?.entry_key
      };
    }
    if (entryType === 'purchase') {
      return {
        Id: latestEntry?.purchases?.[0]?.id,
        Key: latestEntry?.purchases?.[0]?.purchase_key
      };
    }
    if (entryType === 'journal') {
      return {
        Id: latestEntry?.journalFrom?.[0]?.id,
        Key: latestEntry?.journalFrom?.[0]?.entry_key
      };
    }
    return { Id: null, Key: null };
  }, [latestEntry, entryType]);

  const fetchHistory = useCallback(async () => {
    if (!branchId || !Id || !Key) return;

    try {
      setLoading(true);
      const res = await api.get(`/theft-cantrol/updation-history`, {
        params: {
          branchId,
          transaction_id: Id,
          updation_key: Key,
        },
      });

      if (res.data.success) {
        setAllHistory(res.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load history data.");
    } finally {
      setLoading(false);
    }
  }, [branchId, Id, Key]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Render Receipt/Payment Table
  const renderReceiptPaymentTable = (type) => {
    const entry = type === 'receipt' ? latestEntry?.receipts : latestEntry?.payments;
    if (!entry || entry.length === 0) return null;




    return (
      <div className="overflow-x-auto lg:overflow-visible mt-1">
        <table className="w-full table-auto border-separate border-spacing-0">
          <thead>
            <tr className="text-theme-headerBg bg-theme-color border-b border-theme-headerBg">
              <th className="px-1 text-center font-medium border-b border-t border-s text-sm">
                {t("srNumber")}
              </th>
              <th className="px-1 p-[2px] text-start font-medium border-b border-t text-sm">
                {type === 'receipt' ? t("receiptInfo") : t("paymentInfo")}
              </th>
              <th className="px-1 font-medium text-sm border-b border-t">
                {t("description")}
              </th>
              <th className="px-1 text-center font-medium text-sm border-b border-t">
                {t("paymentType")}
              </th>
              <th className="px-1 text-center border-e font-medium text-sm border-b border-t">
                {t("amount")}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr className="align-center bg-theme-tableBg">
              <td className="border-b border-s text-center font-bold text-sm">
                {entry.map((_, i) => (
                  <div key={i}>
                    {i + 1}
                    {i < entry.length - 1 && <hr />}
                  </div>
                ))}
              </td>

              <td className="border-b break-words text-sm cursor-pointer">
                {entry.map((row, i) => (
                  <div className="text-center" key={i}>
                    <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                      <ContactMarquee
                        firstName={row.name || row.ledger_name || "-"}
                        secondName={row.cousinName || row.cousin_name || ""}
                      />
                    </div>
                    <div className="flex gap-2 justify-center w-32 capitalize">
                      <ContactMarquee
                        firstName={row.ledgerSubGroup || row.sub_group_name || "-"}
                        secondName={row.subGroupNameInHindi || row.sub_group_name_in_hindi || ""}
                      />
                    </div>
                    <div className="w-32 capitalize">
                      <ContactMarquee
                        phoneVal={row.phone}
                        addressVal={row.address}
                      />
                    </div>
                    {i < entry.length - 1 && <hr />}
                  </div>
                ))}
              </td>

              <td className="border-b px-1 text-sm">
                {entry.map((row, i) => (
                  <div key={i}>
                    {row.description || "-"}
                    {i < entry.length - 1 && <hr />}
                  </div>
                ))}
              </td>

              <td className="border-b text-center text-sm">
                {entry.map((row, i) => (
                  <div key={i}>
                    {row.paymentType?.name || row.payment_name || "-"}
                    {i < entry.length - 1 && <hr />}
                  </div>
                ))}
              </td>

              <td className="border-b border-e text-center text-sm">
                {entry.map((row, i) => (
                  <div key={i}>
                    {Number(row.amount || 0).toLocaleString("en-US", {
                      maximumFractionDigits: 0,
                    })}
                    {i < entry.length - 1 && <hr />}
                  </div>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render Sales Table
  const renderSalesTable = () => {
    const entry = latestEntry;
    if (!entry?.sales || entry.sales.length === 0) return null;

    const hasSales = entry.sales?.length > 0;
    const hasTransportation = entry.transportation?.length > 0;
    const hasLoading = entry.loading?.length > 0;

    const maxRows = Math.max(
      entry.sales?.length || 0,
      entry.transportation?.length || 0,
      entry.loading?.length || 0,
      1
    );

    return (
      <div className="overflow-x-auto lg:overflow-visible mt-1">
        <table className="w-full table-auto border-collapse overflow-hidden text-nowrap">
          <thead>
            <tr className="bg-theme-headerBg text-theme-color">
              <th colSpan={(hasTransportation || hasLoading) ? "3" : "4"} className="border-e py-[2px] text-center font-medium text-sm">
                {t('sales')}
              </th>
              {hasTransportation && (
                <th colSpan={hasTransportation && !hasLoading ? "3" : "3"} className="border-e text-center font-medium text-sm">
                  {t('transportation')}
                </th>
              )}
              {hasLoading && (
                <th colSpan={3} className="text-center font-medium text-sm">
                  {t('loading/unloading')}
                </th>
              )}
            </tr>
            <tr className="text-theme-headerBg bg-white border-b border-theme-headerBg">
              <th className="border-b px-1 text-start font-medium text-sm w-32">
                {t('customerInfo')}
              </th>
              <th className="border-b px-1 text-center font-medium text-sm">
                {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
              </th>
              <th className="border-b border-e px-1 text-center font-medium text-sm">
                {t('received')} ({t('paymentType')})
              </th>

              {hasTransportation && (
                <>
                  <th className="border-b px-1 text-start font-medium text-sm w-32">
                    {t('vehicle')}
                  </th>
                  <th className="border-b px-1 text-center font-medium text-sm">
                    {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                  </th>
                  <th className="border-b border-e px-1 text-center font-medium text-sm">
                    {t('paid')} ({t('paymentType')})
                  </th>
                </>
              )}

              {hasLoading && (
                <>
                  <th className="border-b px-1 text-start font-medium text-sm w-32">
                    {t('name')}
                  </th>
                  <th className="border-b px-1 text-center font-medium text-sm">
                    {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                  </th>
                  <th className="border-b border-e px-1 text-center font-medium text-sm">
                    {t('paid')} ({t('paymentType')})
                  </th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {[...Array(maxRows)].map((_, rowIndex) => {
              const salesRow = entry.sales?.[rowIndex];
              const transportRow = entry.transportation?.[rowIndex];
              const loadingRow = entry.loading?.[rowIndex];

              return (
                <tr key={rowIndex} className="bg-theme-tableBg">
                  {/* Customer Info */}
                  {rowIndex === 0 && hasSales && (
                    <td rowSpan={maxRows} className="break-words text-sm border-b border-s border-gray-200">
                      {entry.sales.length > 0 && (
                        <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                              <ContactMarquee
                                firstName={entry.sales[0].customer_name}
                                secondName={entry.sales[0].cousinName}
                              />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee
                                firstName={entry.sales[0].ledgerSubGroup}
                                secondName={entry.sales[0].subGroupNameInHindi}
                              />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee
                              phoneVal={entry.sales[0].phone}
                              addressVal={entry.sales[0].address}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  )}

                  {/* Sales Qty */}
                  <td className="border-b text-center text-sm p-0">
                    {salesRow && (
                      <>
                        {Number(salesRow.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        {salesRow.unit} {salesRow.typeOfBricks?.name || ""} @{" "}
                        {Number(salesRow.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        {salesRow.rate && "/"}{Number(salesRow.per_quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        = {Number(salesRow.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </>
                    )}
                  </td>

                  {/* Received */}
                  <td className="border-b border-e text-center text-sm p-0">
                    {salesRow && (
                      <>
                        {Number(salesRow.received).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        {salesRow.received > 0 && salesRow.paymentType && `(${salesRow.paymentType.name})`}
                      </>
                    )}
                  </td>

                  {/* Transportation */}
                  {hasTransportation && (
                    <>
                      <td className="border-b text-center text-sm">
                        {transportRow && (
                          <div>
                            <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                              <ContactMarquee firstName={transportRow.name} secondName={transportRow.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 px-1 capitalize">
                              <ContactMarquee firstName={transportRow.ledgerSubGroup} secondName={transportRow.subGroupNameInHindi} />
                            </div>
                            <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                              <ContactMarquee phoneVal={transportRow.phone} addressVal={transportRow.address} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="border-b text-center text-sm">
                        {transportRow && (
                          <>
                            {Number(transportRow.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {transportRow.unit} {transportRow.typeOfBricks?.name || ""} @{" "}
                            {Number(transportRow.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {transportRow.rate && "/"}{Number(transportRow.per_quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            = {Number(transportRow.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                          </>
                        )}
                      </td>
                      <td className="border-b border-e text-center text-sm">
                        {transportRow && (
                          <>
                            {Number(transportRow.paid).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {transportRow.paid > 0 && transportRow.paymentType && `(${transportRow.paymentType.name})`}
                          </>
                        )}
                      </td>
                    </>
                  )}

                  {/* Loading */}
                  {hasLoading && (
                    <>
                      <td className="border-b text-center text-sm">
                        {loadingRow && (
                          <div>
                            <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                              <ContactMarquee firstName={loadingRow.name} secondName={loadingRow.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={loadingRow.ledgerSubGroup} secondName={loadingRow.subGroupNameInHindi} />
                            </div>
                            <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                              <ContactMarquee phoneVal={loadingRow.phone} addressVal={loadingRow.address} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="border-b text-center text-sm">
                        {loadingRow && (
                          <>
                            {Number(loadingRow.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {loadingRow.unit} {loadingRow.typeOfBricks?.name || ""} @{" "}
                            {Number(loadingRow.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {loadingRow.rate && "/"}{Number(loadingRow.per_quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            = {Number(loadingRow.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                          </>
                        )}
                      </td>
                      <td className="border-b border-e text-center text-sm">
                        {loadingRow && (
                          <>
                            {Number(loadingRow.paid).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {loadingRow.paid > 0 && loadingRow.paymentType && `(${loadingRow.paymentType.name})`}
                          </>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Purchase Table
  const renderPurchaseTable = () => {
    const entry = latestEntry;
    if (!entry?.purchases || entry.purchases.length === 0) return null;

    const hasPurchase = entry.purchases?.length > 0;
    const hasTransportation = entry.transportation?.length > 0;
    const hasLoading = entry.loading?.length > 0;

    const maxRows = Math.max(
      entry.purchases?.length || 0,
      entry.transportation?.length || 0,
      entry.loading?.length || 0,
      1
    );

    return (
      <div className="overflow-x-auto lg:overflow-visible mt-1">
        <table className="w-full table-auto border-collapse overflow-hidden text-nowrap">
          <thead>
            <tr className="bg-theme-headerBg text-theme-color">
              <th colSpan={(hasTransportation || hasLoading) ? "3" : "4"} className="border-e py-[2px] text-center font-medium text-sm">
                {t('purchase')}
              </th>
              {hasTransportation && (
                <th colSpan={hasTransportation && !hasLoading ? "3" : "3"} className="border-e py-[2px] text-center font-medium text-sm">
                  {t('transportation')}
                </th>
              )}
              {hasLoading && (
                <th colSpan={3} className="py-[2px] text-center font-medium text-sm">
                  {t('loading/unloading')}
                </th>
              )}
            </tr>
            <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
              <th className="border-b border-s px-1 text-start font-medium text-sm w-32">{t('vendorInfo')}</th>
              <th className="border-b px-1 text-center font-medium text-sm">
                {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
              </th>
              <th className="border-b border-e px-1 text-center font-medium text-sm">{t('paid')} ({t('paymentType')})</th>

              {hasTransportation && (
                <>
                  <th className="border-b px-1 text-start font-medium text-sm w-32">{t('vehicle')}</th>
                  <th className="border-b px-1 text-center font-medium text-sm">
                    {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                  </th>
                  <th className="border-b border-e px-1 text-center font-medium text-sm">{t('paid')} ({t('paymentType')})</th>
                </>
              )}

              {hasLoading && (
                <>
                  <th className="border-b px-1 text-start font-medium text-sm w-32">{t('name')}</th>
                  <th className="border-b px-1 text-center font-medium text-sm">
                    {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
                  </th>
                  <th className="border-b border-e px-1 text-center font-medium text-sm">{t('paid')} ({t('paymentType')})</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {[...Array(maxRows)].map((_, rowIndex) => {
              const purchase = entry.purchases?.[rowIndex];
              const transport = entry.transportation?.[rowIndex];
              const load = entry.loading?.[rowIndex];

              return (
                <tr key={rowIndex} className="bg-theme-tableBg">
                  {/* Vendor Info */}
                  {rowIndex === 0 && hasPurchase && (
                    <td rowSpan={maxRows} className="border-b break-words border-s text-sm">
                      {entry.purchases.length > 0 && (
                        <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={entry.purchases[0].vendor} secondName={entry.purchases[0].cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={entry.purchases[0].ledgerSubGroup} secondName={entry.purchases[0].subGroupNameInHindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={entry.purchases[0].phone} addressVal={entry.purchases[0].address} />
                          </div>
                        </div>
                      )}
                    </td>
                  )}

                  {/* Purchase Qty */}
                  <td className="border-b text-center text-sm px-1">
                    {purchase && (
                      <>
                        {Number(purchase.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        {purchase.unit} {purchase.items?.name || ""} @{" "}
                        {Number(purchase.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        {purchase.rate && "/"}{Number(purchase.perQuantity === 0 ? purchase.perQuantity : 1).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        = {Number(purchase.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </>
                    )}
                  </td>

                  {/* Paid */}
                  <td className="border-b border-e text-center text-sm px-1">
                    {purchase && (
                      <>
                        {Number(purchase.paid).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                        {purchase.paid > 0 && purchase.paymentType && `(${purchase.paymentType.name})`}
                      </>
                    )}
                  </td>

                  {/* Transportation */}
                  {hasTransportation && (
                    <>
                      <td className="border-b text-center text-sm px-[2px]">
                        {transport && (
                          <div>
                            <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                              <ContactMarquee firstName={transport.name} secondName={transport.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={transport.ledgerSubGroup} secondName={transport.subGroupNameInHindi} />
                            </div>
                            <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                              <ContactMarquee phoneVal={transport.phone} addressVal={transport.address} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="border-b text-center text-sm px-1">
                        {transport && (
                          <>
                            {Number(transport.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {transport.unit} {transport.items?.name || ""} @{" "}
                            {Number(transport.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                            {transport.rate && "/"}{Number(transport.perQuantity === 0 ? transport.perQuantity : 1).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            = {Number(transport.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                          </>
                        )}
                      </td>
                      <td className="border-b border-e text-center text-sm px-1">
                        {transport && (
                          <>
                            {Number(transport.paid).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {transport.paid > 0 && transport.paymentType && `(${transport.paymentType.name})`}
                          </>
                        )}
                      </td>
                    </>
                  )}

                  {/* Loading */}
                  {hasLoading && (
                    <>
                      <td className="border-b text-center text-sm">
                        {load && (
                          <div>
                            <div className="flex gap-2 justify-center w-32 capitalize fw-medium">
                              <ContactMarquee firstName={load.name} secondName={load.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={load.ledgerSubGroup} secondName={load.subGroupNameInHindi} />
                            </div>
                            <div className="p-0 text-gray-600 w-32 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                              <ContactMarquee phoneVal={load.phone} addressVal={load.address} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="border-b text-center text-sm px-1">
                        {load && (
                          <>
                            {Number(load.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {load.unit} {load.items?.name || ""} @{" "}
                            {Number(load.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                            {load.rate && "/"}{Number(load.perQuantity === 0 ? load.perQuantity : 1).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            = {Number(load.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                          </>
                        )}
                      </td>
                      <td className="border-b border-e text-center text-sm px-1">
                        {load && (
                          <>
                            {Number(load.paid).toLocaleString("en-US", { maximumFractionDigits: 0 })}{" "}
                            {load.paid > 0 && load.paymentType && `(${load.paymentType.name})`}
                          </>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Journal Table
  const renderJournalTable = () => {
    const entry = latestEntry;
    if (!entry?.journalFrom || entry.journalFrom.length === 0) return null;

    return (
      <div className="overflow-x-auto lg:overflow-visible mt-1">
        <table className="w-full table-auto border-separate border-spacing-0 overflow-hidden text-nowrap">
          <thead>
            <tr className="bg-theme-headerBg text-theme-color">
              <th colSpan={2} className="border-e  py-[2px] text-center font-medium text-sm">{t('from')}</th>
              <th colSpan={2} className="py-[2px] text-center font-medium text-sm">{t('to')}</th>
            </tr>
            <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
              <th className="border-b px-2 border-s py-[2px] text-start font-medium text-sm">{t('name')}</th>
              <th className="border-b border-e px-1 text-start font-medium text-sm">
                {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
              </th>
              <th className="border-b px-2 text-start font-medium text-sm">{t('name')}</th>
              <th className="border-b border-e px-1 text-start font-medium text-sm">
                {t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr className="align-center bg-theme-tableBg">
              <td className="border-b border-s break-words px-1 text-sm">
                {entry.journalFrom.map((row, i) => (
                  <div className="text-sm flex flex-col" key={i}>
                    <div className="text-center p-0 capitalize">
                      <div className="flex gap-2 justify-center w-44 capitalize fw-medium">
                        <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                      </div>
                      <div className="flex gap-2 justify-center w-44 capitalize">
                        <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                      </div>
                    </div>
                    <div className="p-0 text-gray-600 w-44 text-nowrap overflow-x-scroll custom-scrollbar capitalize">
                      <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                    </div>
                  </div>
                ))}
              </td>

              <td className="border-b border-e text-start text-sm p-0">
                {entry.journalFrom.map((row, i) => (
                  <div className="m-0 p-0" key={i}>
                    {Number(row.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.unit} {row.items?.name || ""} @ {Number(row.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.rate && "/"}{Number(row.perQuantity === 0 ? row.perQuantity : 1).toLocaleString("en-US", { maximumFractionDigits: 0 })} = {Number(row.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}<br />
                    {i < entry.journalFrom.length - 1 && <hr className="my-1" />}
                  </div>
                ))}
              </td>

              <td className="border-b text-center text-sm px-[2px]">
                {entry.journalTo?.map((row, i) => (
                  <div key={i}>
                    <div className="flex gap-2 justify-center w-44 capitalize fw-medium">
                      <ContactMarquee firstName={row.name} secondName={row.cousinName} />
                    </div>
                    <div className="flex gap-2 justify-center w-44 capitalize">
                      <ContactMarquee firstName={row.ledgerSubGroup} secondName={row.subGroupNameInHindi} />
                    </div>
                    <div className="p-0 text-gray-600 w-44 text-nowrap overflow-x-scroll capitalize custom-scrollbar">
                      <ContactMarquee phoneVal={row.phone} addressVal={row.address} />
                    </div>
                    {i < entry.journalTo.length - 1 && <hr className="my-1" />}
                  </div>
                ))}
              </td>

              <td className="border-b border-e text-start px-1 text-sm p-0">
                {entry.journalTo?.map((row, i) => (
                  <div key={i} className="m-0 p-0">
                    {Number(row.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.unit} {row.items?.name || ""} @ {Number(row.rate).toLocaleString("en-US", { maximumFractionDigits: 0 })} {row.rate && "/"}{Number(row.perQuantity === 0 ? row.perQuantity : 1).toLocaleString("en-US", { maximumFractionDigits: 0 })} = {Number(row.total).toLocaleString("en-US", { maximumFractionDigits: 0 })}<br />
                    {i < entry.journalTo.length - 1 && <hr className="my-1" />}
                  </div>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

const groupedHistory = useMemo(() => {
  const map = {};
  const order = [];

  allHistory.forEach((row) => {
    const key = row.comman_entry;

    if (!map[key]) {
      map[key] = {
        comman_entry: key,
        sales: [],
        purchases:[],
        transportation: [],
        loading: [],
        journal_from: [],
        journal_to: [],
        receipt: [],
        payment: [],
        meta: row,
      };
      order.push(key);
    }

    if (row.table_name === "sales") map[key].sales.push(row);
    if (row.table_name === "purchase") map[key].purchases.push(row);
    if (row.table_name === "tranportation" || row.table_name === "transportation")
      map[key].transportation.push(row);
    if (row.table_name === "loading") map[key].loading.push(row);
    if (row.table_name === "journalFrom") map[key].journal_from.push(row);
    if (row.table_name === "journalTo") map[key].journal_to.push(row);
    if (row.table_name === "receipt") map[key].receipt.push(row);
    if (row.table_name === "payment") map[key].payment.push(row);
  });

  return order.map((k) => map[k]);
}, [allHistory]);

  return (
    <div>

      <div className="text-theme-headerBg fw-medium text-start text-sm mb-2">
        Current Entry
      </div>

      {/* Render appropriate table based on entry type */}
      {entryType === 'receipt' && renderReceiptPaymentTable('receipt')}
      {entryType === 'payment' && renderReceiptPaymentTable('payment')}
      {entryType === 'sales' && renderSalesTable()}
      {entryType === 'purchase' && renderPurchaseTable()}
      {entryType === 'journal' && renderJournalTable()}

      {/* ================= HISTORY ================= */}
  {/* ================= HISTORY ================= */}
<div className="text-theme-headerBg fw-medium text-start text-sm pt-3 mb-2">
  Update History
</div>

<div className="overflow-x-auto lg:overflow-visible">
  <table className="w-full table-auto border-collapse overflow-hidden text-nowrap">
    <thead>
      {/* ===== Main Heading Row ===== */}
      <tr className="bg-theme-headerBg text-theme-color">
        

        {(entryType === "sales" || entryType === "purchase") && (
          <>
            <th colSpan={4} className="border-e  py-[2px] text-center font-medium text-sm">
              {entryType === "sales" ?  t("sales") : t("purchase")}
            </th>
            <th colSpan={3} className="border-e py-[2px] text-center font-medium text-sm">
              {t("transportation")}
            </th>
            <th colSpan={3} className="py-[2px] text-center font-medium text-sm">
              {t("loading/unloading")}
            </th>
          </>
        )}

        {(entryType === "receipt" || entryType === "payment") && (
          <>
            <th colSpan={4} className="border-e py-[2px] text-center font-medium text-sm">
              {t(entryType)}
            </th>
          </>
        )}

        {entryType === "journal" && (
          <>
            <th colSpan={3} className="border-e py-[2px] text-center font-medium text-sm">
              {t("from")}
            </th>
            <th colSpan={2} className="py-[2px] text-center font-medium text-sm">
              {t("to")}
            </th>
          </>
        )}

        <th colSpan={2} className="border-e border-s  py-[2px] text-center font-medium text-sm">
          {t("updated by")}
        </th>
        
      </tr>

      {/* ===== Sub Heading Row ===== */}
      <tr className="bg-theme-tableBg text-theme-headerBg border-b border-theme-headerBg">
        {(entryType === "sales" || entryType === "purchase") && (
          <>
          <th className="border-e border-s px-1 py-[2px] text-center font-medium text-sm">
          {t("srNumber")}
        </th>
            <th className="border-b px-1 text-start font-medium text-sm">{ entryType === "sales" ? t("customerInfo") : t("vendorInfo")}</th>
            <th className="border-b px-1 text-center font-medium text-sm">{t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{entryType === "sales" ? t("received") :t("paid") } ({t('paymentType')}) </th>

            <th className="border-b px-1 text-start font-medium text-sm">{t("vehicle")}</th>
            <th className="border-b px-1 text-center font-medium text-sm">{t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{t("paid")} ({t('paymentType')})</th>

            <th className="border-b px-1 text-start font-medium text-sm">{t("name")}</th>
            <th className="border-b px-1 text-center font-medium text-sm">{t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{t("paid")} ({t('paymentType')})</th>
          </>
        )}

        {(entryType === "receipt" || entryType === "payment") && (
          <>
            <th className="border-e border-s px-1 py-[2px] text-center font-medium text-sm">
          {t("srNumber")}
        </th>
            <th className="border-b px-1 text-start font-medium text-sm">{t("ledger")}</th>
            <th className="border-b px-1 text-center font-medium text-sm">{t("amount")}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{t("paymentType")}</th>
          </>
        )}

        {entryType === "journal" && (
          <>
            <th className="border-e border-s px-1 py-[2px] text-center font-medium text-sm">
          {t("srNumber")}
        </th>
            <th className="border-b px-1 text-start font-medium text-sm">{t("name")}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}</th>
            <th className="border-b px-1 text-start font-medium text-sm">{t("name")}</th>
            <th className="border-b border-e px-1 text-center font-medium text-sm">{t('qty')} {t('unit')} {t('item')} @ {t('rate')} {t('perQty')} = {t('total')}</th>
          </>
        )}
        <th className="py-[2px]  px-1 text-center font-medium text-sm">
          {t("name")}
        </th>
         <th  className=" py-[2px] px-1 border-e text-center font-medium text-sm">
          {t("reason")}
        </th>
      </tr>
    </thead>

    <tbody>
      {loading ? (
        <tr>
          <td colSpan={15} className="py-6 text-center">
            <LoadingSpinner size={40} color="#be8024" />
          </td>
        </tr>
      ) : groupedHistory.length > 0 ? (
        groupedHistory.map((group, idx) => (
          <tr key={group.comman_entry} className="bg-theme-tableBg">
            {/* ===== SR ===== */}
            <td className="border-b border-s border-e text-center font-bold text-sm align-middle">
              {groupedHistory.length - idx}
            </td>

            {/* ================= SALES / PURCHASE ================= */}
            {(entryType === "sales") && (
              <>
                {/* Sales */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.sales.map((s, i) => (
                    <div key={i} className="mb-1">
                       <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={s.ledger_name} secondName={s.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={s.sub_group_name} secondName={s.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={s.phone} addressVal={s.address} />
                          </div>
                        </div>
                      
                      {i < group.sales.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.sales.map((s, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-600">
                        {s.quantity} {s.unit} @ {s.rate} = {s.total}
                      </div>
                     
                      {i < group.sales.length - 1 && <hr />}
                    </div>
                  ))}
                </td>
                

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.sales.map((s, i) => (
                    <div key={i}>
                      {s.received > 0 && (
                        <div className="text-xs">
                         {s.received} ({s.payment_type})
                        </div>
                      )}
                      {i < group.sales.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                {/* Transportation */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i} className="mb-1">
                        <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={t.ledger_name} secondName={t.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={t.sub_group_name} secondName={t.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={t.phone} addressVal={t.address} />
                          </div>
                        </div>
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i}>
                       <div className="text-xs text-gray-600">
                        {t.quantity} {t.unit} @ {t.rate} = {t.total}
                      </div>
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i}>
                    {t.paid > 0 && (
                        <div className="text-xs">
                         {t.pais} ({t.payment_type})
                        </div>
                      )}
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                {/* Loading */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i} className="mb-1">
                          <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={l.ledger_name} secondName={l.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={l.sub_group_name} secondName={l.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={l.phone} addressVal={l.address} />
                          </div>
                        </div>
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-600">
                        {l.quantity} {l.unit} @ {l.rate} = {l.total}
                      </div>
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i}>
                     {l.paid > 0 && (
                        <div className="text-xs">
                         {l.paid} ({l.payment_type})
                        </div>
                      )}
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>
              </>
            )}
            {( entryType === "purchase") && (
              <>
                {/* Sales */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.purchases.map((s, i) => (
                    <div key={i} className="mb-1">
                       <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={s.ledger_name} secondName={s.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={s.sub_group_name} secondName={s.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={s.phone} addressVal={s.address} />
                          </div>
                        </div>
                      
                      {i < group.purchases.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.purchases.map((s, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-600">
                        {s.quantity} {s.unit} @ {s.rate} = {s.total}
                      </div>
                     
                      {i < group.purchases.length - 1 && <hr />}
                    </div>
                  ))}
                </td>
                

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.purchases.map((s, i) => (
                    <div key={i}>
                      {s.received > 0 && (
                        <div className="text-xs">
                         {s.received} ({s.payment_type})
                        </div>
                      )}
                      {i < group.purchases.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                {/* Transportation */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i} className="mb-1">
                        <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={t.ledger_name} secondName={t.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={t.sub_group_name} secondName={t.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={t.phone} addressVal={t.address} />
                          </div>
                        </div>
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i}>
                       <div className="text-xs text-gray-600">
                        {t.quantity} {t.unit} @ {t.rate} = {t.total}
                      </div>
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.transportation.map((t, i) => (
                    <div key={i}>
                    {t.paid > 0 && (
                        <div className="text-xs">
                         {t.pais} ({t.payment_type})
                        </div>
                      )}
                      {i < group.transportation.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                {/* Loading */}
                <td className="border-b text-sm p-1 align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i} className="mb-1">
                          <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={l.ledger_name} secondName={l.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={l.sub_group_name} secondName={l.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={l.phone} addressVal={l.address} />
                          </div>
                        </div>
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i}>
                      <div className="text-xs text-gray-600">
                        {l.quantity} {l.unit} @ {l.rate} = {l.total}
                      </div>
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.loading.map((l, i) => (
                    <div key={i}>
                     {l.paid > 0 && (
                        <div className="text-xs">
                         {l.paid} ({l.payment_type})
                        </div>
                      )}
                      {i < group.loading.length - 1 && <hr />}
                    </div>
                  ))}
                </td>
              </>
            )}

            {/* ================= RECEIPT / PAYMENT ================= */}
            {(entryType === "receipt" || entryType === "payment") && (
              <>
                <td className="border-b text-sm p-1 align-middle">
                  {group.receipt.length || group.payment.length
                    ? (group.receipt.length ? group.receipt : group.payment).map((r, i) => (
                        <div key={i} className="mb-1">
                          <div className="fw-medium">{r.ledger_name}</div>
                          <div className="text-xs">{r.phone || "-"}</div>
                          <div className="text-xs text-gray-600">{r.address || "-"}</div>
                          {i < (group.receipt.length || group.payment.length) - 1 && <hr />}
                        </div>
                      ))
                    : ""}
                </td>

                <td className="border-b text-sm text-center align-middle">
                  {(group.receipt.length || group.payment.length
                    ? group.receipt.length
                      ? group.receipt
                      : group.payment
                    : []
                  ).map((r, i) => (
                    <div key={i}>
                      {Number(r.amount || 0).toLocaleString("en-US")}
                      {i < (group.receipt.length || group.payment.length) - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {(group.receipt.length || group.payment.length
                    ? group.receipt.length
                      ? group.receipt
                      : group.payment
                    : []
                  ).map((r, i) => (
                    <div key={i}>
                      {r.payment_type || "-"}
                      {i < (group.receipt.length || group.payment.length) - 1 && <hr />}
                    </div>
                  ))}
                </td>
              </>
            )}

            {/* ================= JOURNAL ================= */}
            {entryType === "journal" && (
              <>
                <td className="border-b text-sm p-1 align-middle">
                  {group.journal_from.map((j, i) => (
                    <div key={i} className="mb-1">
                       <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={j.ledger_name} secondName={j.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={j.sub_group_name} secondName={j.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={j.phone} addressVal={j.address} />
                          </div>
                        </div>
                      {i < group.journal_from.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.journal_from.map((j, i) => (
                    <div key={i}>
                   <div className="text-xs text-gray-600">
                        {j.quantity} {j.unit} @ {j.rate} = {j.total}
                      </div>
                      {i < group.journal_from.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b text-sm p-1 align-middle">
                  {group.journal_to.map((j, i) => (
                    <div key={i} className="mb-1">
                     <div className="text-sm flex flex-col">
                          <div className="text-center p-0 capitalize">
                            <div className="flex gap-2 justify-center w-32 fw-medium capitalize">
                              <ContactMarquee firstName={j.ledger_name} secondName={j.cousinName} />
                            </div>
                            <div className="flex gap-2 justify-center w-32 capitalize">
                              <ContactMarquee firstName={j.sub_group_name} secondName={j.sub_group_name_in_hindi} />
                            </div>
                          </div>
                          <div className="p-0 text-gray-600 text-nowrap w-32 overflow-x-scroll capitalize custom-scrollbar">
                            <ContactMarquee phoneVal={j.phone} addressVal={j.address} />
                          </div>
                        </div>
                      {i < group.journal_to.length - 1 && <hr />}
                    </div>
                  ))}
                </td>

                <td className="border-b border-e text-sm text-center align-middle">
                  {group.journal_to.map((j, i) => (
                    <div key={i}>
                     <div className="text-xs text-gray-600">
                        {j.quantity} {j.unit} @ {j.rate} = {j.total}
                      </div>
                      {i < group.journal_to.length - 1 && <hr />}
                    </div>
                  ))}
                </td>
              </>
            )}
           <td className="border-b  text-sm align-middle text-wrap">
              {group.meta.creator_first_name} {group.meta.creator_last_name}
            </td>
            <td className="border-b border-e text-sm px-1 align-middle max-w-28 text-wrap">
              {group.meta.updation_reason || ""}
            </td>          
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan={15} className="border-b bg-theme-tableBg py-2 text-sm text-center text-gray-500">
            {t("noDataAvailable")}
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

    </div>
  );
}